import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/** biome-ignore-all lint/suspicious/noArrayIndexKey: inevitable with richtext data */ import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Image } from '@lichtblick/photon/atoms/image/image';
import { Li, Ol, Ul } from '@lichtblick/photon/atoms/list/list';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { Video } from '@lichtblick/photon/atoms/video';
import { Fragment } from 'react';
import { isRichText } from './isRichtext';
import { sanitizeStoryblokLink } from './links';
import { RichTextAnchor } from './rich-text-anchor';
import styles from './richtext.module.scss';
const defaultElementMappers = {
    b: ({ children })=>/*#__PURE__*/ _jsx("strong", {
            children: children
        }),
    i: ({ children })=>/*#__PURE__*/ _jsx("em", {
            children: children
        }),
    u: ({ children })=>/*#__PURE__*/ _jsx("u", {
            children: children
        }),
    sup: ({ children })=>/*#__PURE__*/ _jsx("sup", {
            children: children
        }),
    sub: ({ children })=>/*#__PURE__*/ _jsx("sub", {
            children: children
        }),
    code: ({ children })=>/*#__PURE__*/ _jsx("code", {
            children: children
        }),
    s: ({ children })=>{
        if (typeof children !== 'string') {
            return /*#__PURE__*/ _jsx("s", {
                children: children
            });
        }
        const parts = children.split('|');
        if (parts.length !== 2) {
            return /*#__PURE__*/ _jsx("s", {
                children: children
            });
        }
        return /*#__PURE__*/ _jsx("span", {
            className: styles.highlight,
            style: {
                '--replaced-text': `"${parts[1]}"`
            },
            children: /*#__PURE__*/ _jsx("span", {
                children: /*#__PURE__*/ _jsx("span", {
                    children: parts[0]
                })
            })
        });
    },
    h1: ({ children })=>/*#__PURE__*/ _jsx(Headline, {
            renderAs: "h1",
            size: "l",
            children: children
        }),
    h2: ({ children })=>/*#__PURE__*/ _jsx(Headline, {
            renderAs: "h2",
            size: "m",
            children: children
        }),
    h3: ({ children })=>/*#__PURE__*/ _jsx(Headline, {
            renderAs: "h3",
            size: "s",
            children: children
        }),
    h4: ({ children })=>/*#__PURE__*/ _jsx(Headline, {
            renderAs: "h4",
            size: "s",
            children: children
        }),
    h5: ({ children })=>/*#__PURE__*/ _jsx(Headline, {
            renderAs: "h5",
            size: "s",
            children: children
        }),
    h6: ({ children })=>/*#__PURE__*/ _jsx(Headline, {
            renderAs: "h6",
            size: "s",
            children: children
        }),
    p: ({ children, textAlign })=>/*#__PURE__*/ _jsx(Text, {
            renderAs: "p",
            size: "l",
            spacing: true,
            align: textAlign,
            children: children
        }),
    ol: ({ children })=>/*#__PURE__*/ _jsx(Ol, {
            children: children
        }),
    ul: ({ children })=>/*#__PURE__*/ _jsx(Ul, {
            children: children
        }),
    li: ({ children })=>/*#__PURE__*/ _jsx(Li, {
            children: children
        }),
    blockquote: ({ children })=>/*#__PURE__*/ _jsx("blockquote", {
            className: styles.blockquote,
            children: children
        }),
    hr: ()=>/*#__PURE__*/ _jsx("hr", {}),
    table: ({ children })=>/*#__PURE__*/ _jsx("table", {
            className: styles.table,
            children: children
        }),
    tbody: ({ children })=>/*#__PURE__*/ _jsx("tbody", {
            children: children
        }),
    tr: ({ children })=>/*#__PURE__*/ _jsx("tr", {
            children: children
        }),
    th: ({ children, colspan, rowspan })=>/*#__PURE__*/ _jsx("th", {
            colSpan: colspan,
            rowSpan: rowspan,
            children: children
        }),
    td: ({ children, colspan, rowspan })=>/*#__PURE__*/ _jsx("td", {
            colSpan: colspan,
            rowSpan: rowspan,
            children: children
        }),
    a: ({ children, href = '#' })=>{
        return /*#__PURE__*/ _jsx(RichTextAnchor, {
            href: href,
            children: children
        });
    },
    embed: ({ children })=>/*#__PURE__*/ _jsx(_Fragment, {
            children: children
        }),
    embedBlock: ({ children })=>/*#__PURE__*/ _jsx(_Fragment, {
            children: children
        }),
    img: ({ alt, src })=>/*#__PURE__*/ _jsx(Image, {
            description: alt,
            url: {
                mobile: src
            }
        }),
    video: ({ alt, src })=>/*#__PURE__*/ _jsx(Video, {
            description: alt,
            urls: {
                mobile: [
                    src
                ]
            }
        })
};
const markMap = {
    bold: 'b',
    code: 'code',
    italic: 'i',
    underline: 'u',
    subscript: 'sub',
    superscript: 'sup',
    strike: 's'
};
const isLinkMark = (mark)=>mark.type === 'link';
const isTextMark = (mark)=>Object.keys(markMap).includes(mark.type);
const RichTextElement = ({ el, index, mappers })=>{
    let text;
    let linkMark;
    let textMark;
    let TextComponent;
    switch(el.type){
        case 'paragraph':
            return /*#__PURE__*/ _jsx(mappers.p, {
                textAlign: el.attrs?.textAlign,
                children: el.content?.map((child, i)=>/*#__PURE__*/ _jsx(RichTextElement, {
                        el: child,
                        index: i,
                        mappers: mappers
                    }, i))
            });
        case 'blockquote':
            return /*#__PURE__*/ _jsx(mappers.blockquote, {
                children: el.content?.map((child, i)=>/*#__PURE__*/ _jsx(RichTextElement, {
                        el: child,
                        index: i,
                        mappers: mappers
                    }, i))
            });
        case 'hard_break':
            return /*#__PURE__*/ _jsx("br", {});
        case 'text':
            linkMark = el.marks?.find(isLinkMark);
            if (linkMark) {
                if (linkMark.attrs.linktype === 'email') {
                    return /*#__PURE__*/ _jsx(mappers.a, {
                        href: `mailto:${linkMark.attrs.href}`,
                        children: el.text
                    });
                }
                return /*#__PURE__*/ _jsx(mappers.a, {
                    href: sanitizeStoryblokLink(linkMark.attrs.href),
                    children: el.text
                });
            }
            text = el.text.split('\n').map((str, index)=>/*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        index > 0 && /*#__PURE__*/ _jsx("br", {}),
                        str
                    ]
                }, `${str}${index}`));
            textMark = el.marks?.find(isTextMark);
            if (!textMark) {
                return /*#__PURE__*/ _jsx(Fragment, {
                    children: text
                });
            }
            TextComponent = mappers[markMap[textMark.type]];
            return /*#__PURE__*/ _jsx(TextComponent, {
                children: el.text
            });
        case 'ordered_list':
            return /*#__PURE__*/ _jsx(mappers.ol, {
                children: el.content?.map((child, i)=>/*#__PURE__*/ _jsx(RichTextElement, {
                        el: child,
                        index: i,
                        mappers: mappers
                    }, i))
            });
        case 'bullet_list':
            return /*#__PURE__*/ _jsx(mappers.ul, {
                children: el.content?.map((child, i)=>/*#__PURE__*/ _jsx(RichTextElement, {
                        el: child,
                        index: i,
                        mappers: mappers
                    }, i))
            });
        case 'list_item':
            return /*#__PURE__*/ _jsx(mappers.li, {
                i: index,
                children: el.content?.map((child, i)=>/*#__PURE__*/ _jsx(RichTextElement, {
                        el: child,
                        index: i,
                        mappers: mappers
                    }, i))
            });
        case 'heading':
            TextComponent = mappers[`h${el.attrs.level}`];
            return /*#__PURE__*/ _jsx(TextComponent, {
                children: el.content?.map((child, i)=>/*#__PURE__*/ _jsx(RichTextElement, {
                        el: child,
                        index: i,
                        mappers: mappers
                    }, i))
            });
        case 'image':
            return /*#__PURE__*/ _jsx(mappers.img, {
                alt: el.attrs.alt,
                src: el.attrs.src
            });
        case 'table':
            return /*#__PURE__*/ _jsx(mappers.table, {
                children: el.content?.map((row, i)=>/*#__PURE__*/ _jsx(mappers.tr, {
                        children: row.content?.map((cell, cellIndex)=>cell.type === 'tableHeader' ? /*#__PURE__*/ _jsx(mappers.th, {
                                rowspan: cell.attrs.rowspan,
                                colspan: cell.attrs.colspan,
                                children: cell.content?.map((child, index)=>/*#__PURE__*/ _jsx(RichTextElement, {
                                        el: child,
                                        index: index,
                                        mappers: mappers
                                    }, index))
                            }, cellIndex) : /*#__PURE__*/ _jsx(mappers.td, {
                                rowspan: cell.attrs.rowspan,
                                colspan: cell.attrs.colspan,
                                children: cell.content?.map((child, index)=>/*#__PURE__*/ _jsx(RichTextElement, {
                                        el: child,
                                        index: index,
                                        mappers: mappers
                                    }, index))
                            }, cellIndex))
                    }, i))
            });
    }
};
export const RichText = ({ elementMappers, richText })=>{
    if (!isRichText(richText)) {
        return null;
    }
    const mappers = {
        ...defaultElementMappers,
        ...elementMappers
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: richText?.content?.map((child, i)=>/*#__PURE__*/ _jsx(RichTextElement, {
                el: child,
                index: i,
                mappers: mappers
            }, i))
    });
};
const mapRichTextToHtmlElement = (el)=>{
    let text;
    let linkMark;
    let textMark;
    switch(el.type){
        case 'paragraph':
            return `<p>${el.content?.map(mapRichTextToHtmlElement).join('') ?? ''}</p>`;
        case 'hard_break':
            return '<br/>';
        case 'text':
            linkMark = el.marks?.find(isLinkMark);
            if (linkMark) {
                return `<a href="${sanitizeStoryblokLink(linkMark.attrs.href)}">${el.text}</a>`;
            }
            text = el.text.replaceAll('\n', '<br/>');
            textMark = el.marks?.find(isTextMark);
            return textMark ? `<${markMap[textMark.type]}>${text}</${markMap[textMark.type]}>` : `${text}`;
        case 'ordered_list':
            return `<ol>${el.content?.map(mapRichTextToHtmlElement).join('') ?? ''}</ol>`;
        case 'bullet_list':
            return `<ul>${el.content?.map(mapRichTextToHtmlElement).join('') ?? ''}</ul>`;
        case 'list_item':
            return `<li>${el.content?.map(mapRichTextToHtmlElement).join('') ?? ''}</li>`;
        case 'heading':
            return `<h${el.attrs.level}>${el.content?.map(mapRichTextToHtmlElement).join('') ?? ''}</h${el.attrs.level}>`;
        default:
            return '';
    }
};
export const richTextToHtmlMapper = (richText)=>{
    return richText?.content?.map(mapRichTextToHtmlElement).join('') ?? '';
};
export const isRichTextEmpty = (richText)=>{
    return !richText?.content?.length || richText.content.length === 1 && richText.content[0].type === 'paragraph' && !richText.content[0].content;
};

//# sourceMappingURL=RichText.js.map